<template>
  <va-card stripe="primary">
    <SwappingSquaresSpinner v-if="loadingHandle"
      :animation-duration="1000"
      :size="90"
      :color="this.$store.getters.config.palette.primary"
      style="margin: 30px auto;"
    />
    <div class="va-card__body" v-if="!loadingHandle">
      <form style="margin-top: 20px;">
        <div class="row">
          <va-input
            v-model="appSubscription.name"
            type="text"
            :label="$t('subscriptions.name')"
            class="flex xs12"
            :error="!!subscriptionNameErrors.length"
            :error-messages="subscriptionNameErrors"
          />
          <va-input
            v-model="appSubscription.description"
            type="text"
            :label="$t('subscriptions.description')"
            class="flex xs12"
          />
        </div>
        <div class="row">
          <div class="flex xs12">
            <va-select
              :disabled="!paymentMethods.length > 0"
              :label="$t('subscriptions.paymentMethods')"
              :placeholder="paymentMethods.length < 1 ? $t('subscriptions.noPayment') : $t('subscriptions.multiplePayment')"
              v-model="selectedPaymentMethods"
              multiple
              searchable
              textBy="type"
              keyBy="paymentMethodTypeId"
              :options="paymentMethods"
              :error="!!paymentMethodErrors.length"
            />
          </div>
        </div>
        <div class="priceList">
          <va-list class="row">
            <va-item>
              <va-item-section side>
                <fieldset>
                  <va-toggle
                    :label="appSubscription.isPerUnit ? $t('subscriptions.unit') : $t('subscriptions.amount')"
                    v-model="appSubscription.isPerUnit"
                  />
                </fieldset>
              </va-item-section>
              <va-item-section side style="min-width: 200px;">
                <va-select
                  :label="$t('subscriptions.chargePeriodType')"
                  v-model="appSubscriptionType"
                  textBy="description"
                  keyBy="appSubscriptionTypeId"
                  :options="appSubscriptionTypes"
                  :error="!!appSubscriptionTypesErrors.length"
                />
              </va-item-section>
              <va-item-section side>
                <va-input
                  :disabled="!appSubscriptionType"
                  :placeholder="appSubscriptionType ? $t('subscriptions.ratePer') + appSubscriptionType.type : $t('subscriptions.fallbackRate')"
                  :label="appSubscriptionType ? $t('subscriptions.normalRatePer') + appSubscriptionType.type + ' *' : $t('subscriptions.normalRate')"
                  type="number"
                  class="form-control"
                  v-model="appSubscriptionFallbackPrice.price"
                  :error="!!appSubscriptionFallbackPriceError.length"
                  :error-messages="appSubscriptionFallbackPriceError" />
              </va-item-section>
              <va-item-section style="margin-top: -14px;">
                <va-item-label>
                  {{$t('subscriptions.periodTimeChargedPer') + (appSubscription.isPerUnit ? $t('subscriptions.periodUnit') : $t('subscriptions.unitAmount')) + ''}}
                </va-item-label>

                <va-item-label caption>
                  {{appSubscription.isPeriod && appSubscription.isPerUnit ? $t('subscriptions.isPeriodAndIsPerUnit') : appSubscription.isPeriod ? $t('subscriptions.isPeriod') : ''}}
                </va-item-label>
              </va-item-section>
            </va-item>
            <va-item>
              <div class="flex xs12" style="white-space: normal;">{{appSubscription.appSubscriptionPrices.length === 0 ? $t('subscriptions.selectSubscriptionPeriodType') : $t('subscriptions.configureYourTimesheet')}}</div>
            </va-item>
            <draggable
              v-if="appSubscription.appSubscriptionPrices.length !== 0"
              :list="appSubscription.appSubscriptionPrices"
              class="flex xs12"
              :move="checkMove"
            >
              <va-item v-for="(element, idx) in appSubscription.appSubscriptionPrices"
                :key="element.order">
                <va-item-section side>
                  <i class="fa fa-align-justify handle"> {{ element.order + 1 }} </i>
                </va-item-section>
                <va-item-section>
                  <div class="flex xs12">
                    <va-input
                      :placeholder="appSubscriptionType.type ? $t('subscriptions.eg2') + appSubscriptionType.type + 's' : $t('subscriptions.periodForType')"
                      :label="$t('subscriptions.forHowMany') + appSubscriptionType.description + ' ?'"
                      type="number"
                      class="form-control"
                      v-model="element.period"
                      :error="!!element.periodError.length"
                      :error-messages="element.periodError"> </va-input>
                  </div>
                </va-item-section>
                <va-item-section>
                  <va-input
                    :label="$t('subscriptions.price')"
                    :placeholder="$t('subscriptions.chargePrice')"
                    type="number"
                    class="form-control"
                    v-model="element.price"
                    :error="!!element.priceError.length"
                    :error-messages="element.priceError"/>
                </va-item-section>
                <va-item-section side style="padding: 0 0 15px 0;">
                  <va-button-group>
                    <va-button flat small color="gray" @click.prevent="removeAt(idx)" icon="fa fa-trash"> {{ $t('subscriptions.remove') }}</va-button>
                  </va-button-group>
                </va-item-section>
              </va-item>
            </draggable>
          </va-list>
        </div>
        <div class="row" style="padding-bottom: 15px;">
          <div class="flex xs12">
            <va-button v-if="appSubscription.appSubscriptionPrices.length < 1 && appSubscriptionType.appSubscriptionTypeId" slot="footer" flat color="info" @click.prevent="addRow()">{{ $t('subscriptions.addPrice') }}</va-button>
            <va-button v-if="appSubscription.appSubscriptionPrices.length > 0" slot="footer" flat color="gray" @click.prevent="addRow()">{{ $t('subscriptions.addAnotherPrice') }}</va-button>
          </div>
        </div>

        <div class="row" style="padding-left: 10px; margin-bottom: 10px;">
          <fieldset class="xs12">
            <va-checkbox
              :label="$t('subscriptions.defaultSubscription')"
              v-model="appSubscription.isDefault"
            />
          </fieldset>
          <fieldset class="xs12" style="width: 100%;">
            <va-checkbox
              :label="$t('subscriptions.showThisPayment')"
              v-model="appSubscription.isUserShow"
            />
          </fieldset>
          <fieldset class="xs12" style="width: 100%;">
            <va-checkbox
              disabled
              :label="$t('subscriptions.showThisPlan')"
              v-model="appSubscription.isGlobal"
            />
          </fieldset>
        </div>
        <div class="row">
          <div class="flex xs12">
            <va-select
              :disabled="true"
              :label="appSubscription.isGlobal ? $t('subscriptions.selectLocations') : $t('subscriptions.selectLocationsRequired')"
              v-model="selectedLocations"
              :placeholder="locations.length < 1 ? $t('subscriptions.noLocation') : $t('subscriptions.multipleLocationSelect')"
              multiple
              searchable
              textBy="name"
              keyBy="eventId"
              :options="locations"
              :error="!!locationErrors.length"
            />
          </div>
        </div>
        <div class="d-flex justify--center mt-3">
          <va-button @click.prevent="onsubmit()" class="my-0">{{ $t('subscriptions.update') }}</va-button>
        </div>
      </form>
    </div>
  </va-card>
</template>

<script>
import draggable from 'vuedraggable'
import { SwappingSquaresSpinner } from 'epic-spinners'
import errorHandlerMixin from '@/mixin/errorHandlerMixin'

export default {
  name: 'ChargesAppSubscription',
  components: { draggable, SwappingSquaresSpinner },
  mixins: [errorHandlerMixin],
  data () {
    return {
      appSubscription: {
        name: '',
        description: '',
        appSubscriptionTypeId: undefined,
        isWithoutPrice: false,
        isGlobal: false,
        isDefault: false,
        isUserShow: false,
        isPeriod: true,
        isPerUnit: false,
        isFixPrice: false,
        isRequestTip: false,
        appSubscriptionPrices: [],
        eventIds: [],
        overwriters: [],
        appPaymentMethodTypes: [],
        removedPrices: [],
      },
      appSubscriptionPrice: {
        appSubscriptionType: {},
        meta: {
          day: {},
          month: {},
          hour: '',
          week: '',
        },
        isPeriod: false,
        order: 0,
        period: 0,
        price: 0.00,
      },
      locations: [],
      paymentMethods: [],
      appSubscriptionType: '',
      appSubscriptions: [],
      appSubscriptionTypes: [],
      appSubscriptionFallbackPrice: {
        price: 0,
        appSubscriptionPriceId: 0,
      },
      selectedLocations: [],
      appSubscriptionFallbackPriceError: [],
      appSubscriptionTypesErrors: [],
      subscriptionNameErrors: [],
      subscriptionFixPriceErrors: [],
      locationErrors: [],
      paymentMethodErrors: [],
      loadingHandle: true,
      selectedPaymentMethods: [],
    }
  },
  props: {
    appSubscriptionObj: Object,
  },
  methods: {
    checkMove (evt) {
      // TODO handle drag n drop movement
    },
    removeAt (idx) {
      if (this.appSubscription.appSubscriptionPrices[idx].appSubscriptionPriceId !== 0) {
        this.appSubscription.removedPrices.push(this.appSubscription.appSubscriptionPrices[idx].appSubscriptionPriceId)
      }
      this.appSubscription.appSubscriptionPrices.splice(idx, 1)
    },
    addRow () {
      const order = this.appSubscription.appSubscriptionPrices.length
      this.appSubscription.appSubscriptionPrices.push({
        appSubscriptionType: {},
        meta: {
          day: '',
          month: '',
          hour: '',
          week: '',
        },
        periodError: [],
        priceError: [],
        isPeriod: this.appSubscription.appSubscriptionPrices.length > 0 ? this.appSubscription.appSubscriptionPrices[0].isPeriod : false,
        order: order,
        period: 0,
        price: 0.00,
      })
    },
    parseSubscription () {
      const subscription = {
        name: this.appSubscription.name,
        description: this.appSubscription.description,
        isWithoutPrice: this.appSubscription.isWithoutPrice,
        isGlobal: this.appSubscription.isGlobal,
        isDefault: this.appSubscription.isDefault,
        isUserShow: this.appSubscription.isUserShow,
        isPeriod: this.appSubscription.isPeriod,
        isPerUnit: this.appSubscription.isPerUnit,
        isFixPrice: this.appSubscription.isFixPrice,
        isTipRequest: this.appSubscription.isRequestTip,
        appSubscriptionPrices: [],
        appSubscriptionTypeId: this.appSubscriptionType.appSubscriptionTypeId,
        eventIds: [],
        overwriters: [],
        appPaymentMethodTypes: [],
        removedPrices: this.appSubscription.removedPrices,
      }
      const subscriptionPaths = []

      this.appSubscription.appSubscriptionPrices.forEach((item, index) => {
        const metas = []
        subscriptionPaths.push({
          appSubscriptionPriceId: item.appSubscriptionPriceId,
          subscriptionPriceMeta: metas,
          period: Number.parseFloat(item.period),
          isOngoing: false,
          price: item.price ? Number.parseFloat(item.price) : 0.00,
          order: index,
        })
      })

      // add fallback
      subscriptionPaths.push({
        subscriptionPriceMeta: [],
        appSubscriptionPriceId: this.appSubscriptionFallbackPrice.appSubscriptionPriceId,
        period: 1,
        isOngoing: true,
        price: parseFloat(this.appSubscriptionFallbackPrice.price),
        order: this.appSubscription.appSubscriptionPrices.length,
      })
      this.selectedPaymentMethods.forEach(item => {
        subscription.appPaymentMethodTypes.push({
          appPaymentMethodTypeId: item.paymentMethodTypeId,
          description: item.description,
        })
      })

      subscription.appSubscriptionPrices = subscriptionPaths

      this.selectedLocations.forEach(item => {
        subscription.eventIds.push(item.eventId)
      })

      return subscription
    },
    async onsubmit () {
      this.subscriptionNameErrors = this.appSubscription.name ? [] : [this.$t('subscriptions.subscriptionNameError')]
      this.appSubscriptionFallbackPriceError = this.appSubscriptionFallbackPrice ? [] : [this.$t('subscriptions.fallbackPriceError')]
      this.paymentMethodErrors = this.selectedPaymentMethods ? [] : [this.$t('subscriptions.paymentMethodError')]
      this.appSubscriptionTypesErrors = Object.keys(this.appSubscriptionTypes).length !== 0 ? [] : [this.$t('subscriptions.typesError')]
      if (!this.appSubscription.isGlobal) {
        this.locationErrors = this.selectedLocations.length ? [] : [this.$t('subscriptions.locationError')]
      }

      // validate price and period
      let priceContainsErrors = false
      for (let i = 0; this.appSubscription.appSubscriptionPrices.length > i; i++) {
        if (!this.appSubscription.appSubscriptionPrices[i].period || this.appSubscription.appSubscriptionPrices[i].period < 1) {
          this.appSubscription.appSubscriptionPrices[i].periodError = [this.$t('subscriptions.periodError')]
          priceContainsErrors = true
        }
        if (!this.appSubscription.appSubscriptionPrices[i].price || this.appSubscription.appSubscriptionPrices[i].price < 1) {
          this.appSubscription.appSubscriptionPrices[i].priceError = [this.$t('subscriptions.priceError')]
          priceContainsErrors = true
        }
      }

      if (!this.formSubscriptionContainErrors && !priceContainsErrors) {
        return
      }
      this.loadingHandle = true
      const subscriptions = this.parseSubscription()
      if (!subscriptions) {
        return
      }
      this.$store.dispatch('updateAppSubscription', {
        appSubscriptionId: this.appSubscriptionObj.appSubscriptionId,
        message: subscriptions,
      }).then(() => {
        this.loadingHandle = false
      }).catch(error => {
        this.errorHandler(error.response)
        this.loadingHandle = false
      })
    },
    async getTheLocations () {
      await this.$store.dispatch('getLocations')
        .then(resp => this.initlocations())
        .catch(error => {
          this.errorHandler(error.response)
          this.loadingHandle = false
        })
    },
    async initlocations () {
      this.locations.splice(0, this.locations.length, ...this.$store.getters.locations)
    },
    async initAppSubscriptionRequirements () {
      await this.getAppSubscriptionTypeset()
        .then(() => this.getTheLocations())
        .then(() => this.getPaymentMethodTypes())
        .catch(error => {
          this.errorHandler(error.response)
          this.loadingHandle = false
        })
      this.$emit('loader', false)
    },
    async initAppSubscription (appSubscription) {
      this.appSubscriptionTypes.filter(item => {
        if (item.appSubscriptionTypeId === appSubscription.appSubscriptionTypeId) {
          this.appSubscriptionType = item
        }
      })

      appSubscription.events.forEach(item => {
        this.locations.filter(loc => {
          if (loc.eventId === item.eventId) {
            this.selectedLocations.push(item)
          }
        })
      })

      appSubscription.appPaymentMethods.forEach(item => {
        this.paymentMethods.filter(loc => {
          if (loc.paymentMethodTypeId === item.appPaymentMethodTypeId) {
            item.paymentMethodTypeId = item.appPaymentMethodTypeId
            this.selectedPaymentMethods.push(item)
          }
        })
      })

      this.appSubscription.name = appSubscription.name
      this.appSubscription.description = appSubscription.description
      this.appSubscription.appSubscriptionTypeId = appSubscription.appSubscriptionTypeId
      this.appSubscription.isWithoutPrice = appSubscription.isWithoutPrice
      this.appSubscription.isGlobal = appSubscription.isGlobal
      this.appSubscription.isDefault = appSubscription.isDefault
      this.appSubscription.isUserShow = appSubscription.isUserShow
      this.appSubscription.isPeriod = appSubscription.isPeriod
      this.appSubscription.isPerUnit = appSubscription.isPerUnit
      this.appSubscription.isFixPrice = appSubscription.isFixPrice
      appSubscription.appSubscriptionPrices.forEach(item => {
        if (item.isOngoing) {
          this.appSubscriptionFallbackPrice.price = item.price
          this.appSubscriptionFallbackPrice.appSubscriptionPriceId = item.appSubscriptionPriceId
        } else {
          this.appSubscription.appSubscriptionPrices.push({
            meta: {
              day: '',
              month: '',
              hour: '',
              week: '',
            },
            appSubscriptionPriceId: item.appSubscriptionPriceId,
            periodError: [],
            priceError: [],
            order: item.order,
            period: item.period,
            price: item.price,
            isOngoing: item.isOngoing,
          })
        }
      })
      this.loadingHandle = false
    },
    async getPaymentMethodTypes () {
      await this.$store.dispatch('getPaymentMethods')
        .then(resp => this.initPaymentMethodTypes(resp.data.message))
        .catch(error => {
          this.errorHandler(error.response)
          this.loadingHandle = false
        })
    },
    async initPaymentMethodTypes (paymentMethods) {
      this.paymentMethods.splice(0, this.paymentMethods.length, ...paymentMethods)
    },
    async getAppSubscriptionTypeset () {
      if (this.$store.getters.appSubscriptionTypes !== 0) {
        await this.$store.dispatch('getAppSubscriptionTypes')
          .then(resp => this.initSubscriptionTypes())
          .catch(error => {
            this.errorHandler(error.response)
            this.loadingHandle = false
          })
      }
    },
    async initSubscriptionTypes () {
      this.appSubscriptionTypes.splice(0, this.appSubscriptionTypes.length, ...this.$store.getters.appSubscriptionTypes)
    },
  },
  computed: {
    formSubscriptionContainErrors () {
      return !(this.subscriptionNameErrors.length || this.locationErrors.length ||
        this.appSubscriptionFallbackPriceError.length || this.appSubscriptionTypesErrors.length ||
        this.subscriptionFixPriceErrors.length || this.paymentMethodErrors.length)
    },
  },
  mounted () {
    // this.$emit('loader', true)
    this.initAppSubscriptionRequirements().then(() => this.initAppSubscription(this.appSubscriptionObj))
      .catch(error => {
        this.errorHandler(error.response)
        this.loadingHandle = false
      })
  },
}
</script>

<style scoped>
  .hideThem {
    display: none;
  }

  .handle {
    float: left;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .action_size {
    font-size: 18px;
  }

  .close {
    float: right;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .va-list {
    padding: 0;
    margin-bottom: 16px;
    box-shadow: rgba(52, 56, 85, 0.25) 0 2px 5px 4px !important;
  }

  .priceList .va-list {
    padding: 0;
    margin-bottom: 16px;
    box-shadow: none !important;
  }

  .priceList .va-item {
    padding: 0 !important;
  }

  .priceList {
    margin-top: 20px;
  }
</style>
